import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogTitle,
} from "../ui/dialog";
import {
  Clock, Mountain, Activity, Check, X, Plus, Calendar, TrendingUp,
  Utensils, BedDouble, Tag, Loader2, PartyPopper,
} from "lucide-react";
import { toast } from "sonner";
import { SERVICE_LEVELS, typeMeta, MONTHS } from "../../lib/adventureConstants";
import { API } from "../../lib/adventureApi";

export default function ModuleDetailModal({ module, open, onClose, onAdd, added, serviceLevel, locationsById }) {
  const [hero, setHero] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [claimed, setClaimed] = useState(null);
  useEffect(() => { setHero(0); setName(""); setEmail(""); setClaimed(null); }, [module]);
  if (!module) return null;
  const meta = typeMeta(module.type);
  const Icon = meta.icon;
  const gallery = module.gallery?.length ? module.gallery : [module.image];
  const details = module.details || {};
  const basePrice = module.prices[serviceLevel] ?? module.prices.essential;
  const discounted = Math.round(basePrice * 0.9);

  const claimDiscount = async () => {
    if (name.trim().length < 2) { toast.error("Please enter your full name"); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { toast.error("Please enter a valid email"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, module_id: module.id, region: module.region }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setClaimed(data);
      toast.success("10% discount locked in!", { description: `Code ${data.discount_code} sent to ${email}` });
    } catch (_) {
      toast.error("Could not lock in your discount. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0 rounded-md overflow-hidden max-h-[90vh] overflow-y-auto thin-scroll" data-testid="module-detail-modal">
        <DialogTitle className="sr-only">{module.name}</DialogTitle>
        {/* Gallery */}
        <div className="relative">
          <img src={gallery[hero]} alt={module.name} className="w-full h-64 sm:h-72 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
          <span className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: meta.color }}>
            <Icon className="w-3.5 h-3.5" /> {meta.label}
          </span>
          <div className="absolute bottom-4 left-5 right-5">
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-white tracking-tight">{module.name}</h2>
            <p className="text-white/80 text-sm">{locationsById[module.location_id]?.name}</p>
          </div>
          {gallery.length > 1 && (
            <div className="absolute bottom-4 right-5 flex gap-1.5">
              {gallery.map((g, i) => (
                <button key={i} onClick={() => setHero(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${i === hero ? "bg-white" : "bg-white/40"}`} />
              ))}
            </div>
          )}
        </div>

        <div className="p-6 sm:p-8">
          {/* stats */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            {[
              { icon: Clock, label: "Duration", val: `${module.days} ${module.days === 1 ? "day" : "days"}` },
              { icon: Activity, label: "Difficulty", val: module.difficulty },
              { icon: Mountain, label: "Max alt.", val: `${module.elevation_max}m` },
              { icon: TrendingUp, label: "Fitness", val: module.fitness },
            ].map((s) => (
              <div key={s.label} className="border border-sand-dark rounded-sm p-3">
                <s.icon className="w-4 h-4 text-terracotta mb-1" />
                <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{s.label}</p>
                <p className="font-semibold text-sm">{s.val}</p>
              </div>
            ))}
          </div>

          <p className="text-ink-secondary leading-relaxed mb-6">{module.description}</p>

          {/* Day by day schedule with times */}
          {details.schedule?.length > 0 && (
            <div className="mb-6">
              <h3 className="font-mono text-xs uppercase tracking-widest text-moss mb-3 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Day by day · timings
              </h3>
              <div className="space-y-0" data-testid="module-schedule">
                {details.schedule.map((s, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-2.5 h-2.5 rounded-full bg-terracotta mt-1.5 shrink-0" />
                      {i < details.schedule.length - 1 && <div className="w-px flex-1 bg-sand-dark my-1" />}
                    </div>
                    <div className="pb-4">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <p className="font-semibold text-sm">{s.label}</p>
                        <span className="font-mono text-[11px] text-terracotta">{s.time}</span>
                      </div>
                      <p className="text-sm text-ink-secondary leading-relaxed mt-0.5">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Meals */}
          {details.meals && (
            <div className="mb-6 flex items-start gap-3 rounded-sm border border-sand-dark p-3.5 bg-moss-light/40" data-testid="module-meals">
              <Utensils className="w-5 h-5 text-moss shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">Meals</p>
                <p className="text-sm text-ink-secondary leading-relaxed">{details.meals}</p>
              </div>
            </div>
          )}

          {/* Feature gallery with pictures */}
          {details.features?.length > 0 && (
            <div className="mb-6">
              <h3 className="font-mono text-xs uppercase tracking-widest text-moss mb-3 flex items-center gap-1.5">
                <BedDouble className="w-3.5 h-3.5" /> What's included, in detail
              </h3>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="module-features">
                {details.features.map((f, i) => (
                  <div key={i} className="rounded-sm overflow-hidden border border-sand-dark">
                    {f.image && <img src={f.image} alt={f.title} className="w-full h-32 object-cover" />}
                    <div className="p-3">
                      <p className="font-semibold text-sm">{f.title}</p>
                      {f.desc && <p className="text-xs text-ink-secondary leading-relaxed mt-0.5">{f.desc}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* highlights */}
          {module.highlights?.length > 0 && (
            <div className="mb-6">
              <h3 className="font-mono text-xs uppercase tracking-widest text-moss mb-2">Highlights</h3>
              <div className="flex flex-wrap gap-2">
                {module.highlights.map((h) => (
                  <span key={h} className="px-2.5 py-1 rounded-full bg-moss-light text-moss text-xs font-medium">{h}</span>
                ))}
              </div>
            </div>
          )}

          {/* service comparison */}
          <div className="mb-6">
            <h3 className="font-mono text-xs uppercase tracking-widest text-moss mb-2">Service levels</h3>
            <div className="grid grid-cols-3 gap-2">
              {SERVICE_LEVELS.map((s) => (
                <div key={s.id} className={`rounded-sm border p-3 ${serviceLevel === s.id ? "border-terracotta bg-terracotta-light/40" : "border-sand-dark"}`}>
                  <p className="font-semibold text-sm">{s.name}</p>
                  <p className="font-mono font-bold text-lg mt-1">${module.prices[s.id]}</p>
                  <p className="text-[11px] text-ink-muted leading-snug mt-1">{s.blurb}</p>
                </div>
              ))}
            </div>
          </div>

          {/* inclusions / exclusions */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="font-mono text-xs uppercase tracking-widest text-moss mb-2">Included</h3>
              <ul className="space-y-1.5">
                {module.inclusions?.map((inc) => (
                  <li key={inc} className="flex items-start gap-2 text-sm text-ink-secondary">
                    <Check className="w-4 h-4 text-moss shrink-0 mt-0.5" /> {inc}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-mono text-xs uppercase tracking-widest text-ink-muted mb-2">Not included</h3>
              <ul className="space-y-1.5">
                {module.exclusions?.map((ex) => (
                  <li key={ex} className="flex items-start gap-2 text-sm text-ink-muted">
                    <X className="w-4 h-4 shrink-0 mt-0.5" /> {ex}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* seasonal */}
          <div className="mb-6">
            <h3 className="font-mono text-xs uppercase tracking-widest text-moss mb-2 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Best months
            </h3>
            <div className="flex flex-wrap gap-1">
              {MONTHS.map((m, i) => (
                <span key={m} className={`font-mono text-[11px] px-2 py-1 rounded-sm ${
                  module.months.includes(i + 1) ? "bg-moss text-white" : "bg-sand text-ink-muted"
                }`}>{m}</span>
              ))}
            </div>
          </div>

          {/* Discount CTA / lead capture */}
          <div className="mb-4 rounded-md border-2 border-terracotta bg-terracotta-light/50 p-4" data-testid="discount-cta">
            {claimed ? (
              <div className="text-center py-2" data-testid="discount-success">
                <PartyPopper className="w-7 h-7 text-terracotta mx-auto mb-1.5" />
                <p className="font-display font-bold text-lg tracking-tight">Your 10% is locked in!</p>
                <p className="text-sm text-ink-secondary">Use code <span className="font-mono font-bold text-terracotta">{claimed.discount_code}</span> — we've emailed the details to {email}.</p>
                <p className="font-mono text-sm mt-2">Your price: <span className="line-through text-ink-muted">${basePrice}</span> <span className="font-bold text-terracotta text-lg">${discounted}</span> pp</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <Tag className="w-4 h-4 text-terracotta" />
                  <p className="font-display font-bold text-base tracking-tight">Lock in your 10% discount today</p>
                </div>
                <p className="text-xs text-ink-secondary mb-3">
                  Save on this experience — <span className="font-mono line-through text-ink-muted">${basePrice}</span>{" "}
                  <span className="font-mono font-bold text-terracotta">${discounted}</span> pp. Enter your details to reserve the offer.
                </p>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input data-testid="lead-name" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Full name" className="px-3 py-2 text-sm bg-white border border-sand-dark rounded-sm text-ink focus:outline-none focus:ring-2 focus:ring-terracotta" />
                  <input data-testid="lead-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address" className="px-3 py-2 text-sm bg-white border border-sand-dark rounded-sm text-ink focus:outline-none focus:ring-2 focus:ring-terracotta" />
                </div>
                <button data-testid="lead-submit" onClick={claimDiscount} disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-11 rounded-sm font-semibold tracking-wide transition-colors disabled:opacity-50">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Tag className="w-4 h-4" /> Lock in my 10% discount</>}
                </button>
              </>
            )}
          </div>

          <button
            data-testid="detail-add-btn"
            onClick={() => { onAdd(module); onClose(); }}
            disabled={added}
            className={`w-full flex items-center justify-center gap-2 h-12 rounded-sm font-semibold tracking-wide transition-colors ${
              added ? "bg-moss-light text-moss cursor-default" : "bg-terracotta hover:bg-terracotta-hover text-white"
            }`}
          >
            {added ? <><Check className="w-4 h-4" /> Added to your journey</> : <><Plus className="w-4 h-4" /> Add to My Journey</>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
